import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { FaUsers, FaRocket, FaExclamationTriangle, FaCheckCircle, FaDownload, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { CgSpinner } from 'react-icons/cg';
import { useDispatch } from 'react-redux';
import nodeInstance from '../../../../../nodeInstance';
import { setAppError } from '../../../../../store/userSlice';
import { validateProjectGroup, formatForBulkAPI, downloadTemplate } from './excelUtils';

const ProjectGroupSection = ({
  groupedData,
  token,
  onMobilizationStart,
  onMobilizationProgress,
  onMobilizationComplete,
  onMobilizationError,
  onSuccess,
  setMobilizationState
}) => {
  const dispatch = useDispatch();
  
  // State for project validation
  const [validationState, setValidationState] = useState({
    isValidating: false,
    availableProjects: [],
    validatedGroups: {},
    hasValidated: false
  });
  
  // State for individual project mobilization
  const [projectMobilizationState, setProjectMobilizationState] = useState({});

  // Bulk progress overlay state
  const [bulkProgress, setBulkProgress] = useState({
    isRunning: false,
    current: '',
    completed: 0,
    total: 0,
    percent: 0
  });

  // Collapsed state per project — all collapsed by default
  const [collapsedProjects, setCollapsedProjects] = useState({});

  const toggleCollapse = useCallback((projectName) => {
    setCollapsedProjects(prev => ({ ...prev, [projectName]: !prev[projectName] }));
  }, []);

  // Fetch available projects for validation
  const fetchAvailableProjects = useCallback(async () => {
    setValidationState(prev => ({ ...prev, isValidating: true }));
    
    try {
      const response = await nodeInstance.get('/mobilization/getProjects', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.status === 200 && response.data?.data) {
        const projects = response.data.data;
        
        // Validate each project group
        const validatedGroups = {};
        Object.keys(groupedData).forEach(projectName => {
          const projectGroup = groupedData[projectName];
          validatedGroups[projectName] = validateProjectGroup(projectGroup, projects);
        });
        
        setValidationState({
          isValidating: false,
          availableProjects: projects,
          validatedGroups,
          hasValidated: true
        });
        
      } else {
        throw new Error('Failed to fetch projects');
      }
    } catch (error) {
      console.error('Project validation error:', error);
      dispatch(setAppError({
        msg: 'Failed to validate projects. Please try again.',
        color: 'error'
      }));
      
      setValidationState(prev => ({ 
        ...prev, 
        isValidating: false,
        hasValidated: false 
      }));
    }
  }, [groupedData, token, dispatch]);

  // Auto-validate when component mounts or data changes
  useEffect(() => {
    if (Object.keys(groupedData).length > 0 && !validationState.hasValidated) {
      fetchAvailableProjects();
    }
  }, [groupedData, validationState.hasValidated, fetchAvailableProjects]);

  // Mobilize individual project
  // isBulk=true skips the per-project onSuccess call (bulk flow fires it once at the end)
  const mobilizeProject = useCallback(async (projectName, isBulk = false) => {
    const projectGroup = validationState.validatedGroups[projectName];
    
    if (!projectGroup || !projectGroup.isValidProject) {
      dispatch(setAppError({
        msg: 'Project validation failed. Cannot mobilize.',
        color: 'error'
      }));
      return;
    }
    
    setProjectMobilizationState(prev => ({
      ...prev,
      [projectName]: { isProcessing: true, error: null, success: false }
    }));
    
    try {
      const payload = formatForBulkAPI(projectGroup);
      
      const response = await nodeInstance.post('/mobilization/addCandidatesBulk', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.status === 200) {
        setProjectMobilizationState(prev => ({
          ...prev,
          [projectName]: { isProcessing: false, error: null, success: true }
        }));
        
        dispatch(setAppError({
          msg: `Successfully mobilized ${projectGroup.employees.length} employees for ${projectName}`,
          color: 'success'
        }));

        // Only fire onSuccess for single-project mobilization, not during bulk
        if (!isBulk && onSuccess) onSuccess([{ status: 'success', project: projectName }]);

        return { status: 'success', project: projectName, count: projectGroup.employees.length };
      } else {
        throw new Error(response.data?.message || 'Mobilization failed');
      }
    } catch (error) {
      console.error(`Mobilization error for ${projectName}:`, error);
      
      const errorMessage = error.response?.data?.message || error.message || 'Mobilization failed';
      
      setProjectMobilizationState(prev => ({
        ...prev,
        [projectName]: { isProcessing: false, error: errorMessage, success: false }
      }));
      
      dispatch(setAppError({
        msg: `Failed to mobilize ${projectName}: ${errorMessage}`,
        color: 'error'
      }));
      
      return { status: 'error', project: projectName, error: errorMessage };
    }
  }, [validationState.validatedGroups, token, dispatch, onSuccess]);

  // Master mobilize all projects — shows blocking progress overlay, fires onSuccess once at end
  const masterMobilize = useCallback(async () => {
    const validProjects = Object.keys(validationState.validatedGroups).filter(
      projectName => validationState.validatedGroups[projectName].isValidProject
    );
    
    if (validProjects.length === 0) {
      dispatch(setAppError({ msg: 'No valid projects to mobilize', color: 'error' }));
      return;
    }

    setBulkProgress({ isRunning: true, current: validProjects[0], completed: 0, total: validProjects.length, percent: 0 });

    const results = [];

    try {
      for (let i = 0; i < validProjects.length; i++) {
        const projectName = validProjects[i];
        // Show which project is being processed
        setBulkProgress(prev => ({ ...prev, current: projectName }));

        const result = await mobilizeProject(projectName, true);
        results.push(result);

        // Update progress AFTER the call completes
        const completed = i + 1;
        setBulkProgress(prev => ({
          ...prev,
          completed,
          percent: Math.round((completed / validProjects.length) * 100),
          current: validProjects[i + 1] || ''
        }));
      }

      setBulkProgress({ isRunning: false, current: '', completed: validProjects.length, total: validProjects.length, percent: 100 });

      // Fire onSuccess once for the whole batch
      if (onSuccess) onSuccess(results);
      onMobilizationComplete(results);

    } catch (error) {
      console.error('Master mobilization error:', error);
      setBulkProgress(prev => ({ ...prev, isRunning: false }));
      onMobilizationError(error);
    }
  }, [validationState.validatedGroups, mobilizeProject, onMobilizationComplete, onMobilizationError, onSuccess, dispatch]);

  // Computed values
  const validProjectCount = useMemo(() => {
    return Object.values(validationState.validatedGroups).filter(group => group.isValidProject).length;
  }, [validationState.validatedGroups]);
  
  const totalValidEmployees = useMemo(() => {
    return Object.values(validationState.validatedGroups)
      .filter(group => group.isValidProject)
      .reduce((sum, group) => sum + group.employees.length, 0);
  }, [validationState.validatedGroups]);
  
  const hasAnyValidProjects = validProjectCount > 0;

  // Render project group
  const renderProjectGroup = (projectName, projectGroup) => {
    const mobilizationStatus = projectMobilizationState[projectName];
    const isProcessing = mobilizationStatus?.isProcessing || false;
    const hasError = mobilizationStatus?.error;
    const isSuccess = mobilizationStatus?.success;
    const isCollapsed = collapsedProjects[projectName] !== false;

    const headerBg = projectGroup.isValidProject ? 'bg-green-50' : 'bg-red-50';
    const headerBorder = projectGroup.isValidProject ? 'border-green-200' : 'border-red-200';

    return (
      <div key={projectName} className={`border rounded-lg overflow-hidden mb-4 ${headerBorder}`}>

        {/* ── Header row ── */}
        <div className={`${headerBg} border-b ${headerBorder} p-3`}>
          <div className="flex items-center gap-3">

            {/* Status icon */}
            <div className={`shrink-0 p-2 rounded-full ${projectGroup.isValidProject ? 'bg-green-100' : 'bg-red-100'}`}>
              {projectGroup.isValidProject
                ? <FaCheckCircle className="text-green-600" />
                : <FaExclamationTriangle className="text-red-600" />}
            </div>

            {/* Project info — clickable area for collapse */}
            <div
              className="flex-1 min-w-0 cursor-pointer"
              onClick={() => toggleCollapse(projectName)}
            >
              <p className="font-semibold text-gray-800 truncate">{projectName}</p>
              <div className="flex flex-wrap gap-x-4 text-xs text-gray-500 mt-0.5">
                <span>{projectGroup.employees.length} employees</span>
                <span>Client: {projectGroup.client}</span>
                <span>Location: {projectGroup.work_location}</span>
              </div>
            </div>

            {/* Mobilize button */}
            <button
              onClick={() => mobilizeProject(projectName)}
              disabled={!projectGroup.isValidProject || isProcessing || isSuccess}
              style={{
                padding: '4px 10px',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                cursor: (!projectGroup.isValidProject || isProcessing || isSuccess) ? 'not-allowed' : 'pointer',
                border: '1px solid',
                backgroundColor: isSuccess ? '#dcfce7' : !projectGroup.isValidProject ? '#f3f4f6' : isProcessing ? '#dbeafe' : '#2563eb',
                borderColor: isSuccess ? '#86efac' : !projectGroup.isValidProject ? '#d1d5db' : isProcessing ? '#93c5fd' : '#1d4ed8',
                color: isSuccess ? '#15803d' : !projectGroup.isValidProject ? '#9ca3af' : isProcessing ? '#1d4ed8' : '#ffffff',
                whiteSpace: 'nowrap'
              }}
            >
              {isProcessing ? <><CgSpinner className="animate-spin" /><span>Mobilizing...</span></>
                : isSuccess ? <><FaCheckCircle /><span>Completed</span></>
                : <><FaRocket /><span>Mobilize Project</span></>}
            </button>

            {/* Collapse chevron */}
            <button
              onClick={() => toggleCollapse(projectName)}
              className="shrink-0 p-2 rounded hover:bg-black/5 text-gray-500"
            >
              {isCollapsed ? <FaChevronDown /> : <FaChevronUp />}
            </button>
          </div>

          {/* Error badge under header */}
          {hasError && (
            <p className="mt-2 text-xs text-red-600 pl-10">Error: {hasError}</p>
          )}

          {/* Validation errors — always visible */}
          {projectGroup.validationErrors.length > 0 && (
            <div className="mt-2 ml-10 p-2 bg-red-100 border border-red-200 rounded text-xs text-red-700 space-y-0.5">
              {projectGroup.validationErrors.map((err, i) => (
                <p key={i}>• {err}</p>
              ))}
            </div>
          )}
        </div>

        {/* ── Employee table — shown when expanded ── */}
        {!isCollapsed && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Employee ID</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Name</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Nationality</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Arrival Date</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Mobilization Date</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Basic</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Total Salary</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {projectGroup.employees.map((emp, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium text-blue-600">{emp.employee_id}</td>
                    <td className="px-3 py-2">{emp.name}</td>
                    <td className="px-3 py-2">{emp.nationality}</td>
                    <td className="px-3 py-2">{emp.arrival_date}</td>
                    <td className="px-3 py-2">{emp.mobilization_date}</td>
                    <td className="px-3 py-2">{Number(emp.Basicsalary || 0).toLocaleString()}</td>
                    <td className="px-3 py-2 font-medium">{Number(emp.Totalsalary || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  if (validationState.isValidating) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <CgSpinner className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Validating projects...</p>
        </div>
      </div>
    );
  }

  if (!validationState.hasValidated) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 mb-4">Failed to validate projects</p>
        <button
          onClick={fetchAvailableProjects}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Retry Validation
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Bulk progress blocking overlay ── */}
      {bulkProgress.isRunning && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          backgroundColor: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff', borderRadius: 12, padding: '32px 40px',
            minWidth: 360, maxWidth: 480, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <CgSpinner style={{ fontSize: 22, color: '#2563eb', animation: 'spin 1s linear infinite' }} className="animate-spin" />
              <span style={{ fontWeight: 700, fontSize: 16, color: '#1e293b' }}>Bulk Mobilization in Progress</span>
            </div>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>
              Processing {bulkProgress.current}...
            </p>

            {/* Progress bar */}
            <div style={{ background: '#e2e8f0', borderRadius: 99, height: 10, overflow: 'hidden', marginBottom: 10 }}>
              <div style={{
                height: '100%', borderRadius: 99,
                background: '#2563eb',
                width: `${bulkProgress.percent}%`
              }} />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#64748b' }}>
              <span>{bulkProgress.completed} / {bulkProgress.total} projects</span>
              <span>{bulkProgress.percent}%</span>
            </div>
          </div>
        </div>
      )}
      {/* Summary Header */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-blue-800 mb-2">Mobilization Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-blue-600 font-medium">Total Projects:</span>
                <span className="ml-2 text-blue-800">{Object.keys(groupedData).length}</span>
              </div>
              <div>
                <span className="text-blue-600 font-medium">Valid Projects:</span>
                <span className="ml-2 text-blue-800">{validProjectCount}</span>
              </div>
              <div>
                <span className="text-blue-600 font-medium">Total Employees:</span>
                <span className="ml-2 text-blue-800">{totalValidEmployees}</span>
              </div>
              <div>
                <span className="text-blue-600 font-medium">Status:</span>
                <span className={`ml-2 font-medium ${hasAnyValidProjects ? 'text-green-600' : 'text-red-600'}`}>
                  {hasAnyValidProjects ? 'Ready' : 'Validation Failed'}
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={downloadTemplate}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center space-x-2"
            >
              <FaDownload />
              <span>Template</span>
            </button>
            
            <button
              onClick={masterMobilize}
              disabled={!hasAnyValidProjects || bulkProgress.isRunning}
              className={`
                px-6 py-2 rounded-lg font-medium flex items-center space-x-2 transition-all
                ${hasAnyValidProjects && !bulkProgress.isRunning
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }
              `}
            >
              <FaRocket />
              <span>Master Mobilize All ({validProjectCount} Projects)</span>
            </button>
          </div>
        </div>
      </div>
      
      {/* Project Groups */}
      <div className="space-y-4">
        {Object.keys(validationState.validatedGroups).map(projectName => 
          renderProjectGroup(projectName, validationState.validatedGroups[projectName])
        )}
      </div>
      
      {/* No valid projects message */}
      {!hasAnyValidProjects && (
        <div className="text-center py-8 bg-yellow-50 border border-yellow-200 rounded-lg">
          <FaExclamationTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h3 className="font-semibold text-yellow-800 mb-2">No Valid Projects Found</h3>
          <p className="text-yellow-700 mb-4">
            All projects in your Excel file have validation errors. Please fix the errors and try again.
          </p>
          <button
            onClick={downloadTemplate}
            className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
          >
            Download Template
          </button>
        </div>
      )}
    </div>
  );
};

export default ProjectGroupSection;